import React, { useState, useEffect, useRef } from 'react';
import {
  BuildIcon,
  Chip,
  Collapse,
  HomeWorkIcon,
  Input,
  SearchIcon,
  Text,
} from '@billda/design-system';
import { GroupHeader } from '../../../../../../shared/components/grouped-view';
import { PlotPreview } from '../../../../api/sitesApi';
import { formatCurrency } from '../../../../../../shared/utils/currency';
import {
  formatQuantity,
  formatUnitRate,
} from '../../../../../../shared/utils/quantity';

const AUTO_EXPAND_COUNT = 3;

interface PlotPreviewAccordionProps {
  plots: PlotPreview[];
}

const ImportPricingValue = ({
  item,
}: {
  item: PlotPreview['labourItems'][number];
}) => {
  const value =
    item.pricingMode === 'UnitRate'
      ? item.totalQuantity != null && item.unitRate != null && item.unitLabel
        ? `${formatQuantity(item.totalQuantity, item.unitLabel)} · ${formatUnitRate(item.unitRate, item.unitLabel)} · ${formatCurrency(item.value)}`
        : 'Incomplete unit pricing'
      : formatCurrency(item.value);

  return (
    <Text
      variant="body2"
      className="min-w-17.5 text-right font-medium whitespace-nowrap text-secondary tabular-nums"
    >
      {value}
    </Text>
  );
};

const PlotPreviewAccordion: React.FC<PlotPreviewAccordionProps> = ({
  plots,
}) => {
  const [filterText, setFilterText] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const hasAutoExpanded = useRef(false);

  // Auto-expand first N plots on mount
  useEffect(() => {
    if (plots.length > 0 && !hasAutoExpanded.current) {
      hasAutoExpanded.current = true;
      setExpanded(
        new Set(
          plots.slice(0, AUTO_EXPAND_COUNT).map(p => `plot-${p.plotNumber}`)
        )
      );
    }
  }, [plots]);

  const toggle = (key: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const filteredPlots = filterText
    ? plots.filter(
        p =>
          p.plotNumber.toLowerCase().includes(filterText.toLowerCase()) ||
          p.name.toLowerCase().includes(filterText.toLowerCase()) ||
          p.labourItems.some(li =>
            li.name.toLowerCase().includes(filterText.toLowerCase())
          )
      )
    : plots;

  return (
    <div data-testid="plot-preview">
      {plots.length > 5 && (
        <div className="mb-4">
          <Input
            inputSize="sm"
            placeholder="Filter plots..."
            value={filterText}
            onChange={e => setFilterText(e.target.value)}
            aria-label="Filter plots"
            startAdornment={
              <SearchIcon
                className="size-4 text-content-secondary"
                aria-hidden="true"
              />
            }
          />
        </div>
      )}

      <div className="overflow-hidden rounded-2xl border border-border-default">
        {filteredPlots.map(plot => {
          const plotKey = `plot-${plot.plotNumber}`;
          const isExpanded = expanded.has(plotKey);
          const plotTotal = plot.labourItems.reduce(
            (sum, item) => sum + item.value,
            0
          );

          return (
            <div key={plotKey}>
              <GroupHeader
                icon={HomeWorkIcon}
                label={`${plot.plotNumber} — ${plot.name}`}
                count={plot.labourItems.length}
                expanded={isExpanded}
                onToggle={() => toggle(plotKey)}
                secondaryText={
                  <Text
                    variant="body2"
                    className="font-medium whitespace-nowrap text-content-secondary tabular-nums"
                  >
                    {formatCurrency(plotTotal)}
                  </Text>
                }
              />
              <Collapse in={isExpanded} unmountOnExit>
                <div>
                  {plot.labourItems.map((item, idx) => (
                    <div
                      key={`${plot.plotNumber}-${item.name}-${idx}`}
                      className="flex min-h-9 items-center gap-2 py-1.5 pr-3 pl-14 hover:bg-surface-raised sm:pr-4"
                    >
                      <BuildIcon
                        className="size-4 shrink-0 text-content-secondary"
                        aria-hidden="true"
                      />
                      <Text variant="body2" className="flex-1 font-medium">
                        {item.name}
                      </Text>
                      {item.isMissingDefinition && (
                        <Chip
                          label="New"
                          size="sm"
                          color="warning"
                          className="h-5 text-xs"
                        />
                      )}
                      <ImportPricingValue item={item} />
                    </div>
                  ))}
                </div>
              </Collapse>
            </div>
          );
        })}

        {filteredPlots.length === 0 && (
          <div className="py-6 text-center">
            <Text variant="body2" color="secondary">
              No plots match your filter
            </Text>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlotPreviewAccordion;
